(function () {
  let isApplying = false;

  function decodeHtmlEntities(text) {
    if (!text) return text;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  function parseStrings(strings) {
    const parsed = {};

    for (const [key, value] of Object.entries(strings || {})) {
      parsed[key] = typeof value === 'string' ? decodeHtmlEntities(value) : value;
    }

    return parsed;
  }

  function formatMessage(template, replacements = {}) {
    if (!template) return '';

    let message = decodeHtmlEntities(template);

    for (const [key, value] of Object.entries(replacements)) {
      message = message.replaceAll(`[${key}]`, value);
    }

    return message;
  }

  function setMessage(messageElement, text, tone = 'default') {
    if (!messageElement) return;

    messageElement.textContent = text;

    if (tone === 'success') {
      messageElement.style.color = 'green';
    } else if (tone === 'error') {
      messageElement.style.color = '#f05832';
    } else {
      messageElement.style.color = '#333';
    }
  }

  function setLoadingState(button, applyLabel, isLoading, strings) {
    if (!button) return;

    button.disabled = isLoading;
    button.setAttribute('aria-busy', String(isLoading));
    button.textContent = isLoading ? strings.applying || 'Applying...' : applyLabel;
  }

  function normalizeCode(code) {
    return String(code || '')
      .trim()
      .toLowerCase();
  }

  function getDiscountCodeEntry(cart, code) {
    const normalizedCode = normalizeCode(code);
    if (!normalizedCode) return null;

    return (
      (cart.discount_codes || []).find(
        (discount) => normalizeCode(discount.code) === normalizedCode
      ) || null
    );
  }

  function getApplicableDiscountCodes(cart) {
    return (cart.discount_codes || [])
      .filter((discount) => discount.applicable === true)
      .map((discount) => discount.code)
      .filter(Boolean);
  }

  function hasActiveDiscountApplication(cart, code) {
    const normalizedCode = normalizeCode(code);
    if (!normalizedCode) return false;

    const cartLevelDiscounts = cart.cart_level_discount_applications || [];
    if (
      cartLevelDiscounts.some((discount) => {
        const title = normalizeCode(discount.title);
        return (
          title === normalizedCode ||
          title.endsWith(` ${normalizedCode}`) ||
          title.startsWith(`${normalizedCode} `)
        );
      })
    ) {
      return true;
    }

    return (cart.items || []).some((item) =>
      (item.line_level_discount_allocations || []).some((allocation) => {
        const title = normalizeCode(allocation.discount_application?.title);
        return (
          title === normalizedCode ||
          title.endsWith(` ${normalizedCode}`) ||
          title.startsWith(`${normalizedCode} `)
        );
      })
    );
  }

  function isDiscountCodeApplied(cart, code) {
    const discountEntry = getDiscountCodeEntry(cart, code);

    // Shopify keeps invalid codes in discount_codes with applicable: false.
    if (discountEntry && typeof discountEntry.applicable === 'boolean') {
      return discountEntry.applicable === true;
    }

    return hasActiveDiscountApplication(cart, code);
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Cart fetch failed: ${response.status}`);
    }

    return response.json();
  }

  async function applyDiscountCode(code) {
    const response = await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ discount: code }),
    });

    const cart = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        cart.description || cart.message || `Discount update failed: ${response.status}`
      );
    }

    return cart;
  }

  async function restoreDiscountCodes(codes) {
    const discount = Array.isArray(codes) ? codes.filter(Boolean).join(',') : '';

    await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ discount }),
    });
  }

  async function refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
    if (!response.ok) {
      throw new Error(`Cart drawer refresh failed: ${response.status}`);
    }

    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const selectors = ['cart-drawer-items', '.cart-drawer__footer'];

    for (const selector of selectors) {
      const targetElement = cartDrawer.querySelector(selector);
      const sourceElement = html.querySelector(selector);

      if (targetElement && sourceElement) {
        targetElement.replaceWith(sourceElement);
      }
    }
  }

  async function refreshCartPage() {
    const cartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');

    if (!cartItems && !cartFooter) {
      window.location.reload();
      return;
    }

    const sectionIds = [];
    if (cartItems?.dataset.id) sectionIds.push(cartItems.dataset.id);
    if (cartFooter?.dataset.id) sectionIds.push(cartFooter.dataset.id);
    sectionIds.push('cart-icon-bubble');

    const response = await fetch(`${routes.cart_url}?sections=${sectionIds.join(',')}`);
    if (!response.ok) {
      throw new Error(`Cart page refresh failed: ${response.status}`);
    }

    const sections = await response.json();

    if (cartItems?.dataset.id && sections[cartItems.dataset.id]) {
      const html = new DOMParser().parseFromString(sections[cartItems.dataset.id], 'text/html');
      const source = html.querySelector('.js-contents');
      const target = cartItems.querySelector('.js-contents');
      if (source && target) target.innerHTML = source.innerHTML;
    }

    if (cartFooter?.dataset.id && sections[cartFooter.dataset.id]) {
      const html = new DOMParser().parseFromString(sections[cartFooter.dataset.id], 'text/html');
      const source = html.querySelector('.js-contents');
      const target = cartFooter.querySelector('.js-contents');
      if (source && target) target.innerHTML = source.innerHTML;
    }

    if (sections['cart-icon-bubble']) {
      const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const source = html.querySelector('.shopify-section') || html.body;
      const target =
        document.getElementById('shopify-section-cart-icon-bubble') ||
        document.getElementById('cart-icon-bubble');

      if (source && target) target.innerHTML = source.innerHTML;
    }
  }

  async function refreshCartUI(isDrawer) {
    if (isDrawer) {
      await refreshCartDrawer();
      return;
    }

    await refreshCartPage();
  }

  async function handleApplyDiscount(container) {
    if (!container || isApplying) return;

    const input = container.querySelector('[id^="discount-code-input"]');
    const button = container.querySelector('[id^="apply-discount-button"]');
    const message = container.querySelector('[id^="discount-message"]');
    const strings = parseStrings(window.discountStrings || {});
    const applyLabel = strings.apply || button?.textContent?.trim() || 'Apply';
    const isDrawer =
      container.closest('cart-drawer') !== null || container.closest('.drawer') !== null;
    const code = input?.value?.trim() || '';

    if (!input || !button) return;

    if (!code) {
      setMessage(message, strings.emptyError || 'Please enter a discount code.', 'error');
      return;
    }

    isApplying = true;
    setLoadingState(button, applyLabel, true, strings);
    setMessage(message, strings.applyingMessage || 'Applying discount...');

    try {
      const currentCart = await fetchCart();
      const previouslyApplicableCodes = getApplicableDiscountCodes(currentCart);

      if (isDiscountCodeApplied(currentCart, code)) {
        setMessage(message, formatMessage(strings.duplicate, { code }), 'error');
        return;
      }

      const updatedCart = await applyDiscountCode(code);
      const isApplied = isDiscountCodeApplied(updatedCart, code);

      if (!isApplied) {
        await restoreDiscountCodes(previouslyApplicableCodes);
        setMessage(message, formatMessage(strings.invalid, { code }), 'error');
        return;
      }

      await refreshCartUI(isDrawer);

      const refreshedContainer =
        (isDrawer
          ? document.querySelector('cart-drawer .cart__discount')
          : document.querySelector('#main-cart-footer .cart__discount')) || container;
      const refreshedInput = refreshedContainer.querySelector('[id^="discount-code-input"]');
      const refreshedMessage = refreshedContainer.querySelector('[id^="discount-message"]');
      const refreshedButton = refreshedContainer.querySelector('[id^="apply-discount-button"]');

      if (refreshedInput) refreshedInput.value = '';
      setMessage(refreshedMessage, formatMessage(strings.success, { code }), 'success');
      setLoadingState(refreshedButton, applyLabel, false, strings);
    } catch (error) {
      setMessage(message, strings.error || 'Could not apply discount.', 'error');
      console.error(error);
    } finally {
      isApplying = false;
      setLoadingState(button, applyLabel, false, strings);
    }
  }

  function getApplyButton(eventTarget) {
    if (!(eventTarget instanceof Element)) return null;
    return eventTarget.closest('[id^="apply-discount-button"]');
  }

  const TAP_MOVE_THRESHOLD_PX = 12;
  let activeTouchTap = null;

  function clearActiveTouchTap() {
    activeTouchTap = null;
  }

  function isElementUnderPoint(element, clientX, clientY) {
    const topElement = document.elementFromPoint(clientX, clientY);
    if (!(topElement instanceof Element)) return false;
    return element === topElement || element.contains(topElement);
  }

  // On mobile, prevent input blur from canceling the tap, but only apply on a real tap
  // (pointerup without meaningful movement), not on scroll/drag gestures.
  document.addEventListener(
    'pointerdown',
    (event) => {
      const button = getApplyButton(event.target);
      if (!button || button.disabled) return;
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;

      event.preventDefault();

      activeTouchTap = {
        pointerId: event.pointerId,
        button,
        startX: event.clientX,
        startY: event.clientY,
        isMoved: false,
      };
    },
    { capture: true, passive: false }
  );

  document.addEventListener(
    'pointermove',
    (event) => {
      if (!activeTouchTap || event.pointerId !== activeTouchTap.pointerId) return;

      const deltaX = Math.abs(event.clientX - activeTouchTap.startX);
      const deltaY = Math.abs(event.clientY - activeTouchTap.startY);

      if (deltaX > TAP_MOVE_THRESHOLD_PX || deltaY > TAP_MOVE_THRESHOLD_PX) {
        activeTouchTap.isMoved = true;
      }
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      if (!activeTouchTap || event.pointerId !== activeTouchTap.pointerId) return;

      const { button, isMoved } = activeTouchTap;
      clearActiveTouchTap();

      if (isMoved) return;
      if (button.disabled) return;
      if (!isElementUnderPoint(button, event.clientX, event.clientY)) return;

      event.preventDefault();
      event.stopPropagation();

      handleApplyDiscount(button.closest('.cart__discount'));
    },
    { capture: true, passive: false }
  );

  document.addEventListener(
    'pointercancel',
    (event) => {
      if (!activeTouchTap || event.pointerId !== activeTouchTap.pointerId) return;
      clearActiveTouchTap();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = getApplyButton(event.target);
      if (!button || button.disabled) return;

      // Touch/pen is handled via pointerup tap detection.
      if (event.pointerType === 'touch' || event.pointerType === 'pen') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      handleApplyDiscount(button.closest('.cart__discount'));
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;

    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.matches('[id^="discount-code-input"]')) return;

    event.preventDefault();

    const container = input.closest('.cart__discount');
    handleApplyDiscount(container);
  });
})();
